<app-nav-bar></app-nav-bar>
<app-side-bar></app-side-bar>
<app-settings-modal></app-settings-modal>

<main class="reviews-page">
  <section class="reviews-header">
    <div>
      <h1>{{ 'REVIEWS_TITLE' | translate }}</h1>
      <p>{{ 'REVIEWS_SUBTITLE' | translate }}</p>
    </div>
  </section>

  <section class="reviews-card" aria-labelledby="reviews-filters-title">
    <div class="section-title">
      <h2 id="reviews-filters-title">{{ 'FILTERS' | translate }}</h2>
    </div>

    <div class="filters-grid">
      <div class="field field--wide">
        <label for="review-search">{{ 'SEARCH' | translate }}</label>
        <input
          id="review-search"
          class="form-control"
          type="text"
          [(ngModel)]="search"
          (keyup.enter)="getReviews(true)"
          [placeholder]="'SEARCH_REVIEWS_PLACEHOLDER' | translate"
          [disabled]="loading" />
      </div>

      <div class="field">
        <label for="review-status">{{ 'STATUS' | translate }}</label>
        <select id="review-status" class="form-select" [(ngModel)]="status" (change)="getReviews(true)" [disabled]="loading">
          <option *ngFor="let option of statusOptions" [ngValue]="option.value">
            {{ option.labelKey | translate }}
          </option>
        </select>
      </div>

      <div class="field">
        <label for="review-component">{{ 'COMPONENT' | translate }}</label>
        <select id="review-component" class="form-select" [(ngModel)]="componentId" (change)="getReviews(true)" [disabled]="loading">
          <option [ngValue]="''">{{ 'ALL' | translate }}</option>
          <option *ngFor="let component of reviewComponents" [ngValue]="component.componentId">
            {{ component.componentName || component.componentTypeName }}
          </option>
        </select>
      </div>

      <div class="filter-actions">
        <button class="btn btn-primary" type="button" (click)="getReviews(true)" [disabled]="loading">
          <i class="bi bi-funnel" aria-hidden="true"></i>
          {{ 'FILTER' | translate }}
        </button>
      </div>
    </div>
  </section>

  <section class="reviews-card reviews-results-card">
    <div class="loading" *ngIf="loading || actionLoading" aria-live="polite">
      <div class="spinner-border" role="status" aria-hidden="true"></div>
      <span>{{ 'LOADING_REVIEWS' | translate }}</span>
    </div>

    <div class="empty-state" *ngIf="!loading && reviews.length === 0">
      {{ (hasActiveFilters() ? 'NO_REVIEWS_FILTERED' : 'NO_REVIEWS') | translate }}
    </div>

    <div class="table-responsive" *ngIf="!loading && reviews.length">
      <table class="reviews-table">
        <colgroup>
          <col class="date-column" />
          <col class="name-column" />
          <col class="email-column" />
          <col class="rating-column" />
          <col class="title-column" />
          <col class="comment-column" />
          <col class="status-column" />
          <col class="actions-column" />
        </colgroup>

        <thead>
          <tr>
            <th scope="col">{{ 'DATE' | translate }}</th>
            <th scope="col">{{ 'REVIEWER_NAME' | translate }}</th>
            <th scope="col">{{ 'REVIEWER_EMAIL' | translate }}</th>
            <th scope="col">{{ 'RATING' | translate }}</th>
            <th scope="col">{{ 'REVIEW_TITLE' | translate }}</th>
            <th scope="col">{{ 'REVIEW_COMMENT' | translate }}</th>
            <th scope="col">{{ 'STATUS' | translate }}</th>
            <th scope="col">{{ 'ACTIONS' | translate }}</th>
          </tr>
        </thead>

        <tbody>
          <tr *ngFor="let review of reviews" [class.pending-review-row]="review.reviewStatus === REVIEW_STATUS_PENDING">
            <td class="date-cell">
              <span>{{ review.createdAt | date:'shortDate' }}</span>
              <small>{{ review.createdAt | date:'shortTime' }}</small>
            </td>

            <td class="name-cell">{{ getReviewerName(review) }}</td>

            <td class="email-cell">
              <span class="email-value" [title]="getReviewerEmail(review)">
                {{ getReviewerEmail(review) }}
              </span>
            </td>

            <td class="rating-cell">
              <span class="rating-pill">{{ review.rating || '-' }} / {{ getReviewMaxRating(review) }}</span>
            </td>

            <td class="title-cell">
              <span class="title-preview" [title]="review.reviewTitle || '-'">
                {{ review.reviewTitle || '-' }}
              </span>
            </td>

            <td class="comment-cell">
              <span class="comment-preview" [title]="review.reviewComment || '-'">
                {{ review.reviewComment || '-' }}
              </span>
            </td>

            <td class="status-cell">
              <span class="review-status" [ngClass]="getStatusClass(review.reviewStatus)">
                {{ getStatusKey(review.reviewStatus) | translate }}
              </span>
            </td>

            <td class="actions-cell">
              <div class="dropdown review-actions">
                <button
                  class="btn btn-secondary dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                  data-bs-boundary="viewport"
                  aria-expanded="false"
                  [attr.aria-label]="'ACTIONS' | translate"
                  [disabled]="actionLoading">
                  {{ 'ACTIONS' | translate }}
                </button>

                <ul class="dropdown-menu">
                  <li *ngIf="review.reviewStatus !== REVIEW_STATUS_APPROVED && review.reviewStatus !== REVIEW_STATUS_ARCHIVED">
                    <button type="button" class="dropdown-item" (click)="approveReview(review)">
                      {{ 'APPROVE_REVIEW' | translate }}
                    </button>
                  </li>

                  <li *ngIf="review.reviewStatus !== REVIEW_STATUS_REJECTED && review.reviewStatus !== REVIEW_STATUS_ARCHIVED">
                    <button type="button" class="dropdown-item text-danger" (click)="confirmReviewAction(review, 'reject')">
                      {{ 'REJECT_REVIEW' | translate }}
                    </button>
                  </li>

                  <li *ngIf="review.reviewStatus !== REVIEW_STATUS_ARCHIVED">
                    <button type="button" class="dropdown-item" (click)="confirmReviewAction(review, 'archive')">
                      {{ 'ARCHIVE_REVIEW' | translate }}
                    </button>
                  </li>

                  <li *ngIf="review.reviewStatus === REVIEW_STATUS_ARCHIVED">
                    <span class="dropdown-item disabled">{{ 'NO_AVAILABLE_ACTIONS' | translate }}</span>
                  </li>
                </ul>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="reviews-pagination" *ngIf="!loading && reviews.length">
      <button type="button" class="btn btn-link" [disabled]="counter === 0 || loading" (click)="previousPage()">
        {{ 'PREVIOUS' | translate }}
      </button>

      <span class="pagination-indicator">{{ getCurrentPage() }} / {{ getTotalPages() }}</span>

      <button type="button" class="btn btn-link" [disabled]="reviews.length < amount || loading" (click)="nextPage()">
        {{ 'NEXT' | translate }}
      </button>
    </div>
  </section>
</main>

<app-confirmation-modal
  *ngIf="pendingAction"
  [title]="(pendingAction === 'reject' ? 'CONFIRM_REJECT_REVIEW' : 'CONFIRM_ARCHIVE_REVIEW') | translate"
  [message]="(pendingAction === 'reject' ? 'REJECT_REVIEW_WARNING' : 'ARCHIVE_REVIEW_WARNING') | translate"
  [confirmText]="(pendingAction === 'reject' ? 'REJECT_REVIEW' : 'ARCHIVE_REVIEW') | translate"
  [cancelText]="'CANCEL' | translate"
  (response)="handleConfirmAction($event)">
</app-confirmation-modal>

<div class="modal component-confirm-modal fade" id="reviewDetailModal" tabindex="-1">
  <div class="modal-dialog component-modal-dialog" role="document">
    <div class="modal-content component-modal-content review-detail-modal-content">
      <div class="modal-body component-modal-body" *ngIf="selectedReview">
        <h3 class="text-center">{{ 'REVIEW_DETAIL' | translate }}</h3>

        <div class="review-detail-grid">
          <div>
            <strong>{{ 'REVIEWER_NAME' | translate }}</strong>
            <p>{{ getReviewerName(selectedReview) }}</p>
          </div>

          <div>
            <strong>{{ 'REVIEWER_EMAIL' | translate }}</strong>
            <p>{{ getReviewerEmail(selectedReview) }}</p>
          </div>

          <div>
            <strong>{{ 'RATING' | translate }}</strong>
            <p>{{ selectedReview.rating || '-' }} / {{ getReviewMaxRating(selectedReview) }}</p>
          </div>

          <div>
            <strong>{{ 'STATUS' | translate }}</strong>
            <p>{{ getStatusKey(selectedReview.reviewStatus) | translate }}</p>
          </div>
        </div>

        <div class="review-detail-comment">
          <strong>{{ 'REVIEW_TITLE' | translate }}</strong>
          <p>{{ selectedReview.reviewTitle || '-' }}</p>

          <strong>{{ 'REVIEW_COMMENT' | translate }}</strong>
          <p>{{ selectedReview.reviewComment || '-' }}</p>
        </div>
      </div>

      <div class="modal-buttons">
        <button type="button" class="btn btn-confirm" (click)="closeModal()">
          {{ 'CLOSE' | translate }}
        </button>
      </div>
    </div>
  </div>
</div>

<div class="modal component-confirm-modal fade" id="successReviewModal" tabindex="-1">
  <div class="modal-dialog component-modal-dialog" role="document">
    <div class="modal-content component-modal-content">
      <div class="modal-body component-modal-body">
        <h3 class="text-center">{{ 'SUCCESS' | translate }}</h3>
        <p class="text-center">{{ 'REVIEW_SAVED_SUCCESS' | translate }}</p>
      </div>

      <div class="modal-buttons">
        <button type="button" class="btn btn-confirm" (click)="closeModal()">
          {{ 'CLOSE' | translate }}
        </button>
      </div>
    </div>
  </div>
</div>

<div class="modal component-confirm-modal fade" id="errorReviewModal" tabindex="-1">
  <div class="modal-dialog component-modal-dialog" role="document">
    <div class="modal-content component-modal-content">
      <div class="modal-body component-modal-body">
        <h3 class="text-center">{{ 'ERROR' | translate }}</h3>
        <p class="text-center">{{ 'ERROR_SAVING_REVIEW' | translate }}</p>
      </div>

      <div class="modal-buttons">
        <button type="button" class="btn btn-confirm" (click)="closeModal()">
          {{ 'CLOSE' | translate }}
        </button>
      </div>
    </div>
  </div>
</div>